import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Conversation } from "../../types/Conversation.ts";
import {
  fetchConversation,
  fetchConversationEvaluation,
  updateConversationEvaluation
} from "../../services/conversations.service.ts";
import StyledButton from "../../components/Button.tsx";
import DetailBox from "../../components/DetailBox.tsx";
import { PageContainer, PageTitle } from "../../components/Layout.tsx";
import AdminFeedbackHistoryModal from "../../components/AdminFeedbackHistoryModal.tsx";
import { parseApiError } from "../../utils/errorHandler.ts";

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Loading = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: #001f52;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    color: #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const Error = styled.div`
  text-align: center;
  padding: 3rem;
  color: #d32f2f;
  font-size: 1.2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    color: #ff6b6b;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const FeedbackMessage = styled.div<{ $variant: 'error' | 'success' }>`
  flex: 1;
  min-width: 0;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  border-radius: 6px;
  border-left: 4px solid;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  ${props => props.$variant === 'error' ? `
    background-color: #ffebee;
    color: #c62828;
    border-left-color: #c62828;

    .dark-mode & {
      background-color: #3a1f1f;
      color: #ff6b6b;
      border-left-color: #ff6b6b;
    }
  ` : `
    background-color: #e8f5e9;
    color: #2e7d32;
    border-left-color: #2e7d32;

    .dark-mode & {
      background-color: #1f2e1f;
      color: #81c784;
      border-left-color: #81c784;
    }
  `}
`;

const LayoutGrid = styled.div`
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  margin-top: 1rem;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const LeftColumn = styled.div`
  flex: 0 0 360px;
  max-width: 380px;
`;

const RightColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const MessagesContainer = styled.div`
  margin-top: 0;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  border-left: 4px solid #64da4c;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 75%;
  min-width: 120px;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: ${props => (props.$isUser ? "#e6f4ff" : "#f5f5f5")};
  color: #001f52;
  font-size: 0.95rem;
  line-height: 1.4;
  border-top-left-radius: ${props => (props.$isUser ? "12px" : "4px")};
  border-top-right-radius: ${props => (props.$isUser ? "4px" : "12px")};
  ${props => props.$isUser ? "margin-left: auto;" : "margin-right: auto;"}
  transition: background 0.3s ease, color 0.3s ease;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;

  .dark-mode & {
    background: ${props => (props.$isUser ? "#2a3a4e" : "#1f1f2e")};
    color: #e0e0e0;
  }
`;

const MarkdownContent = styled.div`
  & > *:first-child {
    margin-top: 0;
  }
  
  & > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0.5rem 0;
    line-height: 1.6;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 1rem 0 0.5rem 0;
    font-weight: 600;
    line-height: 1.3;
  }

  h1 { font-size: 1.5rem; }
  h2 { font-size: 1.3rem; }
  h3 { font-size: 1.1rem; }
  h4 { font-size: 1rem; }

  ul, ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin: 0.25rem 0;
  }

  code {
    background: rgba(0, 0, 0, 0.1);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #001f52;
  }

  pre {
    background: rgba(0, 0, 0, 0.1);
    padding: 0.75rem 1rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.75rem 0;
    
    code {
      background: none;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid #64da4c;
    padding-left: 1rem;
    margin: 0.75rem 0;
    color: #555;
    font-style: italic;
  }

  a {
    color: #0066cc;
    text-decoration: underline;
    transition: color 0.2s ease;
    
    &:hover {
      color: #004499;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.75rem 0;
  }

  th, td {
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 0.5rem;
    text-align: left;
  }

  th {
    background: rgba(0, 0, 0, 0.05);
    font-weight: 600;
  }

  hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 1rem 0;
  }

  .dark-mode & {
    code {
      background: rgba(255, 255, 255, 0.1);
      color: #e0e0e0;
    }

    pre {
      background: rgba(255, 255, 255, 0.1);
    }

    blockquote {
      color: #bbb;
      border-left-color: #64da4c;
    }

    a {
      color: #4da6ff;
      
      &:hover {
        color: #66b3ff;
      }
    }

    th, td {
      border-color: rgba(255, 255, 255, 0.1);
    }

    th {
      background: rgba(255, 255, 255, 0.05);
    }

    hr {
      border-top-color: rgba(255, 255, 255, 0.1);
    }
  }
`;

const MessageWrapper = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: ${props => (props.$isUser ? "flex-end" : "flex-start")};
`;

const MessageMeta = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #999;
  }
`;

const AdminCard = styled.div`
  margin-top: 1.5rem;
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 31, 82, 0.1);
  border-left: 4px solid #64da4c;
  transition: background 0.3s ease, box-shadow 0.3s ease;

  .dark-mode & {
    background: #2a2a3e;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const AdminTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
  font-size: 0.95rem;
  resize: vertical;
  color: #001f52;
  background: white;
  transition: border-color 0.3s ease, color 0.3s ease, background 0.3s ease;

  .dark-mode & {
    background: #1f1f2e;
    border-color: #3a3a4e;
    color: #e0e0e0;
  }

  &:focus {
    outline: none;
    border-color: #64da4c;
    box-shadow: 0 0 0 3px rgba(100, 218, 76, 0.1);
  }
`;

const SectionTitle = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #001f52;
  font-size: 1.5rem;
  font-weight: 700;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const FeedbackSlotInline = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const StarRatingContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const StarsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const Star = styled.button<{ $filled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: 2rem;
  color: ${props => props.$filled ? '#ffc107' : '#e0e0e0'};
  transition: transform 0.1s ease;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const RatingLabel = styled.div`
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 0.5rem;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 0.5rem;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  color: #001f52;
  cursor: pointer;
  transition: color 0.3s ease;

  .dark-mode & {
    color: #e0e0e0;
  }
`;

const RadioInput = styled.input`
  width: 1rem;
  height: 1rem;
  accent-color: #64da4c;
  cursor: pointer;
`;

/**
 * Converts plain URLs in text to markdown links
 * Handles: http://, https://, file extensions (.pdf, .jpeg, .jpg, .png, etc.), and relative paths
 */
const convertUrlsToMarkdownLinks = (text: string): string => {
  if (!text) return text;
  
  const markdownLinks: string[] = [];
  const markdownLinkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let protectedText = text.replace(markdownLinkPattern, (match) => {
    const placeholder = `__MARKDOWN_LINK_${markdownLinks.length}__`;
    markdownLinks.push(match);
    return placeholder;
  });
  
  const urlPattern = /(https?:\/\/[^\s)<>"]+|www\.[^\s)<>"]+|\/[^\s)<>"]*\.(pdf|jpeg|jpg|png|gif|webp|svg|mp4|mp3|doc|docx|xls|xlsx)[^\s)<>"]*|[a-zA-Z0-9][^\s)<>"]*\.(pdf|jpeg|jpg|png|gif|webp|svg|mp4|mp3|doc|docx|xls|xlsx)(?:\?[^\s)<>"]*)?)/gi;

  protectedText = protectedText.replace(urlPattern, (match) => {
    if (match.startsWith('__MARKDOWN_LINK_')) {
      return match;
    }
    
    let url = match.trim();
    url = url.replace(/[.,;:!?]+$/, '');
    
    if (url.startsWith('www.')) {
      url = `https://${url}`;
    }
    
    if (url.startsWith('/') && !url.startsWith('//')) {
      url = `${window.location.origin}${url}`;
    }
    
    const displayText = match.trim().replace(/[.,;:!?]+$/, '');
    return `[${displayText}](${url})`;
  });
  
  markdownLinks.forEach((link, index) => {
    protectedText = protectedText.replace(`__MARKDOWN_LINK_${index}__`, link);
  });
  
  return protectedText;
};

export default function ConversationDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminCommentDraft, setAdminCommentDraft] = useState<string>("");
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const [useForTrainingDraft, setUseForTrainingDraft] = useState<boolean | undefined>(undefined);
  const [showCommentHistory, setShowCommentHistory] = useState(false);

  useEffect(() => {
    if (!id) {
      setError("Samtale ID mangler");
      setLoading(false);
      return;
    }

    const conversationId = parseInt(id);
    setLoading(true);
    setError(null);

    fetchConversation(conversationId)
      .then((found) => {
        setConversation(found);
        setError(null);
      })
      .catch((err) => {
        setError(parseApiError(err));
      })
      .finally(() => {
        setLoading(false);
      });

    fetchConversationEvaluation(conversationId)
      .then((evaluation) => {
        const comment = evaluation.comment ?? "";
        setAdminCommentDraft(comment);
        setSelectedRating(evaluation.rating ?? null);
        setUseForTrainingDraft(evaluation.useForTraining ?? undefined);
      })
      .catch(() => {
      });
  }, [id]);

  useEffect(() => {
    if (feedback?.type !== 'success') return;
    const timer = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(timer);
  }, [feedback]);

  if (loading) {
    return <Loading>Indlæser samtale...</Loading>;
  }

  if (error || !conversation) {
    return (
      <PageContainer>
        <Error>{error || "Samtale ikke fundet"}</Error>
        <StyledButton onClick={() => navigate(-1)} style={{ marginTop: "1rem" }}>← Tilbage</StyledButton>
      </PageContainer>
    );
  }

  const messages = conversation.conversationJson?.messages ?? [];

  const handleSaveAdminComment = async () => {
    if (!conversation) return;
    setSaving(true);
    setFeedback(null);
    try {
      const response = await updateConversationEvaluation(conversation.id, {
        adminComment: adminCommentDraft,
        rating: selectedRating ?? undefined,
        useForTraining: useForTrainingDraft,
      });
      setAdminCommentDraft(response.comment ?? "");
      setSelectedRating(response.rating ?? null);
      setUseForTrainingDraft(response.useForTraining ?? undefined);
      setFeedback({ type: 'success', message: 'Feedback er gemt' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: 'Kunne ikke gemme' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <Header>
        <PageTitle noMarginBottom>Samtale detaljer</PageTitle>
        <StyledButton onClick={() => navigate(-1)}>← Tilbage</StyledButton>
      </Header>

      <LayoutGrid>
        <LeftColumn>
          <DetailBox
            title="Generel information"
            details={[
              { 
                label: "nr.", 
                value: conversation.id 
              },
              { 
                label: "Kategori struktur", 
                value: conversation.hierarchyPath && conversation.hierarchyPath.length > 0
                  ? conversation.hierarchyPath.join(" > ")
                  : "Ingen kategori"
              },
              { 
                label: "Oprettet", 
                value: (() => {
                  if (!conversation.createdAt) return "-";
                  try {
                    const d = new Date(conversation.createdAt);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = String(d.getFullYear()).slice(-2);
                    return `${day}-${month}-${year}`;
                  } catch {
                    return conversation.createdAt;
                  }
                })()
              },
              { label: "Bruger vurdering", value: conversation.userRating || "Ingen vurdering" },
              { label: "Bruger kommentar", value: conversation.userComment || "Ingen kommentar" },
            ]}
          />

          <AdminCard style={{ marginTop: "1.5rem" }}>
            <SectionTitle>Admin feedback</SectionTitle>
            <RatingLabel>Admin Kommentar</RatingLabel>
            <AdminTextArea
              value={adminCommentDraft}
              onChange={(e) => setAdminCommentDraft(e.target.value)}
              placeholder="Skriv din kommentar til samtalen..."
            />

            <StarRatingContainer>
              <RatingLabel>Admin Vurdering (1-5 stjerner)</RatingLabel>
              <StarsContainer>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    $filled={selectedRating !== null && star <= selectedRating}
                    onClick={() => setSelectedRating(selectedRating === star ? null : star)}
                    disabled={saving}
                    title={selectedRating === star ? "Fjern vurdering" : `${star} stjerne${star > 1 ? 'r' : ''}`}
                  >
                    ★
                  </Star>
                ))}
              </StarsContainer>
            </StarRatingContainer>

            <div style={{ marginTop: "1rem" }}>
              <RatingLabel>Brug til træning?</RatingLabel>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="useForTraining"
                    checked={useForTrainingDraft === true}
                    onChange={(e) => e.target.checked && setUseForTrainingDraft(true)}
                    onClick={() => useForTrainingDraft === true && setUseForTrainingDraft(undefined)}
                  />
                  Ja
                </RadioLabel>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="useForTraining"
                    checked={useForTrainingDraft === false}
                    onChange={(e) => e.target.checked && setUseForTrainingDraft(false)}
                    onClick={() => useForTrainingDraft === false && setUseForTrainingDraft(undefined)}
                  />
                  Nej
                </RadioLabel>
              </RadioGroup>
            </div>

            <ButtonRow>
              <StyledButton 
                onClick={handleSaveAdminComment} 
                disabled={saving}
              >
                Gem
              </StyledButton>
              <FeedbackSlotInline>
                {feedback && (
                  <FeedbackMessage $variant={feedback.type}>
                    {feedback.message}
                  </FeedbackMessage>
                )}
              </FeedbackSlotInline>
            </ButtonRow>

            <StyledButton
              onClick={() => setShowCommentHistory(true)}
              disabled={saving}
              style={{
                marginTop: "1rem",
                backgroundColor: "#7c3aed",
                color: "white",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.3)"
              }}
              onMouseEnter={(e) => {
                if (!saving) e.currentTarget.style.backgroundColor = "#6d28d9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#7c3aed";
              }}
            >
              Se alle admin feedback
            </StyledButton>
          </AdminCard>
        </LeftColumn>

        <RightColumn>
          {messages.length > 0 && (
            <MessagesContainer>
              <PageTitle as="h2" noMarginBottom>
                Samtale
              </PageTitle>
              {messages.map((msg, idx) => {
                const isUser = msg.role === "user";
                const label =
                  msg.role === "assistant"
                    ? "Assistent"
                    : msg.role === "user"
                    ? "Bruger"
                    : msg.role;

                return (
                  <MessageWrapper key={idx} $isUser={isUser}>
                    <MessageMeta>{label}</MessageMeta>
                    <MessageBubble $isUser={isUser}>
                      {isUser ? (
                        msg.content
                      ) : (
                        <MarkdownContent>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ node, href, children, ...props }) => {
                                const linkUrl = href || '';
                                let finalUrl = linkUrl;
                                if (linkUrl.startsWith('/') && !linkUrl.startsWith('//')) {
                                  finalUrl = `${window.location.origin}${linkUrl}`;
                                } else if (linkUrl.startsWith('www.')) {
                                  finalUrl = `https://${linkUrl}`;
                                }
                                
                                return (
                                  <a
                                    href={finalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    {...props}
                                  >
                                    {children}
                                  </a>
                                );
                              },
                            }}
                          >
                            {convertUrlsToMarkdownLinks(msg.content)}
                          </ReactMarkdown>
                        </MarkdownContent>
                      )}
                    </MessageBubble>
                  </MessageWrapper>
                );
              })}
            </MessagesContainer>
          )}
        </RightColumn>
      </LayoutGrid>

      {conversation && (
        <AdminFeedbackHistoryModal
          isOpen={showCommentHistory}
          onClose={() => setShowCommentHistory(false)}
          conversationId={conversation.id}
          type="comment"
        />
      )}
    </PageContainer>
  );
}